import {
  Checkbox,
  Paper,
  Skeleton,
  Stack,
  styled,
  Table,
  TableBody,
  TableBodyProps,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import * as React from 'react';

// Component
import { debounce, isEmpty, isFunction, isNumber, map } from 'lodash';
import { useCallback } from 'react';
import TableHeader from './TableHeader';
import TableToolbar from './TableToolbar';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import { useTranslation } from 'react-i18next';
import { renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';

const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    borderBottom: `1px dashed ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
}));

const HEADER_HEIGHT = 57;

export interface ICustomTableProps<T> {
  id?: string;
  selectable?: boolean;
  isHover?: boolean;
  data: T[];
  configColumn: ConfigCell<T>[];
  dataKey: keyof T | ((item: T) => ExpectedAny);
  size?: 'small' | 'medium';
  maxRow?: number;
  handleSort?: ({ sortBy, sort }: { sortBy: string; sort: OrderType }) => void;
  handleFilter?: () => void;
  page?: number;
  pageSize?: number;
  onChangePage?: (e: number) => void;
  onChangePageSize?: (e: number) => void;
  onRowClick?: (data: T) => void;
  loading?: boolean;
  isPagination?: boolean;
  totalRecords?: number;
  rowsPerPageOptions?: number[];
  tableMaxHeight?: number;
  styledRow?: (data: T) => TableBodyProps['sx'];
  sxTableContainer?: Record<string, string>;
  minWidth?: number;
  pagingTooltipsText?: string;
}

export default function CustomTable<T>({
  selectable,
  isHover = true,
  data,
  configColumn,
  dataKey,
  size = 'medium',
  maxRow = 50,
  handleSort,
  handleFilter,
  page = 0,
  pageSize = 10,
  onChangePage,
  onChangePageSize,
  onRowClick,
  loading = false,
  isPagination = true,
  totalRecords,
  rowsPerPageOptions = [5, 10, 25],
  tableMaxHeight,
  styledRow,
  sxTableContainer = {},
  minWidth = 650,
  pagingTooltipsText,
}: ICustomTableProps<T>) {
  const theme = useTheme();
  const [maxHeight, setMaxHeight] = React.useState(0 as number | undefined);
  const [selected, setSelected] = React.useState<readonly T[]>([]);
  const [order, setOrder] = React.useState<OrderType>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('');
  const [selectedPage, setSelectedPage] = React.useState(page);
  const { t } = useTranslation();

  React.useEffect(() => setSelectedPage(page), [page]);

  const refBody = React.useRef<ExpectedAny>(null);

  const dataKeyValueGetter = useCallback(
    (item: T) => {
      switch (typeof dataKey) {
        case 'string':
          return item[dataKey];
        case 'function':
          return dataKey(item);
        default:
          throw 'Unspecified dataKey';
      }
    },
    [dataKey],
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    if (isFunction(onChangePage)) {
      onChangePage(newPage);
    }
  };

  const debounceChangePage = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFunction(onChangePage)) {
      onChangePage(Number(e.target.value));
    }
  }, 1000);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isFunction(onChangePageSize)) {
      onChangePageSize(parseInt(event.target.value, 10));
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    handleSort?.({ sortBy: property, sort: isAsc ? 'desc' : 'asc' });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data?.map((n) => n);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: T) => {
    if (!selectable) return;

    const selectedIndex = selected.findIndex((item) => dataKeyValueGetter(item) === dataKeyValueGetter(id));
    let newSelected: readonly T[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleRowClick = (event: React.MouseEvent<unknown>, data: T) => {
    handleClick(event, data);
    onRowClick?.(data);
  };

  const isSelected = (id: ExpectedAny) =>
    selected.findIndex((item) => dataKeyValueGetter(item) === dataKeyValueGetter(id)) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows = page > 0 ? Math.max(0, (1 + page) * pageSize - data.length) : 0;

  React.useEffect(() => {
    if (isEmpty(data)) {
      setMaxHeight(undefined);
      return;
    }
    let headerHeight = HEADER_HEIGHT;
    const header = document.getElementById('custom-table_header');
    if (header) {
      headerHeight = header.offsetHeight + 1;
    }
    if (isNumber(refBody?.current?.clientHeight) && data.length > 0) {
      const heightPerRow = refBody?.current?.clientHeight / data.length;
      setMaxHeight(heightPerRow * maxRow + headerHeight);
    } else {
      setMaxHeight(headerHeight);
    }
  });

  const loadingSkeleton = new Array(tableMaxHeight ? Math.ceil(tableMaxHeight / 60) : 5).fill(0).map((_, index) => {
    return (
      <TableRow key={index}>
        {map(configColumn, (config) => (
          <StyledBodyCell key={config.id}>
            <Skeleton />
          </StyledBodyCell>
        ))}
      </TableRow>
    );
  });

  const renderEmptyData = () => {
    if (!loading && isEmpty(data)) {
      return emptyData;
    }
    return null;
  };

  const emptyData = new Array(1).fill(0).map((key: number) => (
    <TableRow key={key}>
      {map(configColumn, (config) => (
        <StyledBodyCell key={config.id}>
          <Typography color={theme.palette.text.disabled}>--</Typography>
        </StyledBodyCell>
      ))}
    </TableRow>
  ));

  const renderCellWithRowspan = (config: ConfigCell<T>, i: number, row: T, cellKey: string) => {
    const defaultCell = (
      <StyledBodyCell
        onClick={() => config.onClick && config.onClick(row)}
        key={cellKey}
        align={config.align || 'left'}
        padding={config.disablePadding ? 'none' : 'normal'}
        sortDirection={orderBy === config.id ? order : false}
        sx={{ ...(isFunction(config.styledCell) && config.styledCell?.(row, theme)) }}
      >
        {config.cell(row)}
      </StyledBodyCell>
    );

    if (!config.rowSpan) return defaultCell;
    const rowSpan = config.rowSpan(row);
    const isRenderCellWithRowspan = i % rowSpan === 0;
    if (!isRenderCellWithRowspan) return undefined;

    return (
      <StyledBodyCell
        rowSpan={rowSpan}
        key={cellKey}
        align={config.align || 'left'}
        padding={config.disablePadding ? 'none' : 'normal'}
        sortDirection={orderBy === config.id ? order : false}
        sx={{ ...(isFunction(config.styledCell) && config.styledCell?.(row, theme)) }}
      >
        {config.cell(row)}
      </StyledBodyCell>
    );
  };

  return (
    <Paper sx={{ width: '100%' }}>
      {handleFilter && <TableToolbar numSelected={selected.length} />}
      <TableContainer sx={{ maxHeight: tableMaxHeight || maxHeight, ...sxTableContainer }} component={Paper}>
        <Table
          sx={{
            minWidth: minWidth,
            '& .MuiTableRow-root.MuiTableRow-hover:hover': {
              backgroundColor: theme.palette.background.paper1,
            },
            width: '100%',
            tableLayout: 'fixed',
          }}
          stickyHeader
          aria-label="simple table"
          size={size}
        >
          <TableHeader
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            handleSelectAllClick={handleSelectAllClick}
            handleRequestSort={handleRequestSort}
            rowCount={data.length}
            configColumn={configColumn}
            selectable={selectable}
          />

          <TableBody ref={refBody}>
            {loading && loadingSkeleton}

            {renderEmptyData()}

            {!loading &&
              map(data, (row, indexRow) => {
                const isItemSelected = isSelected(row);
                const labelId = `enhanced-table-checkbox-${indexRow}`;
                return (
                  <TableRow
                    key={`column-row-${indexRow}`}
                    hover={isHover}
                    onClick={(event) => handleRowClick(event, row)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    selected={isItemSelected}
                    sx={{ cursor: isHover ? 'pointer' : 'default', ...(isFunction(styledRow) && styledRow?.(row)) }}
                  >
                    {selectable && (
                      <StyledBodyCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </StyledBodyCell>
                    )}
                    {map(configColumn, (config, i) => {
                      const cellKey = config.id + indexRow + i;
                      return renderCellWithRowspan(config, indexRow, row, cellKey);
                    })}
                  </TableRow>
                );
              })}
            {/* {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (size === 'small' ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )} */}
          </TableBody>
        </Table>
      </TableContainer>
      {isPagination && (
        <Stack alignItems={'center'} justifyContent={'end'} direction={'row'} spacing={2}>
          <TablePagination
            labelDisplayedRows={({ from, to, count }) => {
              return (
                <Stack flexDirection={'row'} alignItems={'center'} gap={0.5}>
                  {`${from}-${to} of ${count !== -1 ? renderNumberOrDefault(count) : `more than ${to}`}`}
                  {pagingTooltipsText && (
                    <TooltipFormLabel sx={{ verticalAlign: 'middle' }} title={t(pagingTooltipsText)} />
                  )}
                </Stack>
              );
            }}
            disabled={loading}
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={totalRecords ? totalRecords : 0}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          <TextField
            type="number"
            inputProps={{ min: 1 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              debounceChangePage(e);
              setSelectedPage(parseInt(e.target.value));
            }}
            value={selectedPage}
            label="Page"
            sx={{ width: 80 }}
            size="small"
          />
        </Stack>
      )}
    </Paper>
  );
}
